import {tokens, colors, radii} from '@commercial-helios/ui/theme'
import {
    Root,
    Trigger,
    Value,
    Icon,
    Portal,
    Content,
    Viewport,
    Item,
    ItemText,
} from '@radix-ui/react-select'
import type {PropsWithChildren} from 'react'
import React from 'react'
import styled from 'styled-components'

import navigateDownIcon from '@helios/shared/images/icons/navigate-down.svg'
import {inputStyles, Label} from '@helios/shared/styles/inputs'
import {Text, text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

type Option<TValue extends string> = {
    value: TValue | ''
    label: string | JSX.Element
}

export interface Props<TValue extends string> {
    onChange: (newValue: TValue) => void
    options: Option<TValue>[]
    value: string
    label?: string
    placeholder: string
    error?: string | null
    id?: string
    required?: boolean
    'data-testid'?: string
}

const Select = <TValue extends string>({
    onChange,
    options,
    value,
    label,
    placeholder,
    error,
    required,
    'data-testid': dataTestId,
}: PropsWithChildren<Props<TValue>>) => {
    return (
        <SelectWrapper>
            {label && <Label $required={required}>{label}</Label>}

            <Root value={value || undefined} onValueChange={onChange} required={required}>
                <SelectTrigger aria-label={label} data-testId={dataTestId}>
                    <Value placeholder={placeholder} />
                    <Icon>
                        <Arrow />
                    </Icon>
                </SelectTrigger>

                <Portal>
                    <SelectContent position="popper">
                        <Viewport>
                            {options.map((option) => (
                                <SelectOption
                                    key={option.value}
                                    value={option.value}
                                    {...(dataTestId && {
                                        'data-testid': `${dataTestId}-option-${option.value}`,
                                    })}
                                >
                                    <ItemText>{option.label}</ItemText>
                                </SelectOption>
                            ))}
                        </Viewport>
                    </SelectContent>
                </Portal>
            </Root>

            {error && (
                <Text variant="label-base" color="error" data-testid={`${dataTestId}-error`}>
                    {error}
                </Text>
            )}
        </SelectWrapper>
    )
}

export const SelectWrapper = styled.div`
    display: grid;
    grid-gap: ${tokens.spacing8};
`

export const Arrow = styled.span`
    display: block;
    width: 12px;
    height: 12px;
    background-image: url(${navigateDownIcon});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
`

export const SelectTrigger = styled(Trigger)`
    ${inputStyles}

    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    cursor: default;
    user-select: none;
    color: ${colors.textInput.default.foreground};

    &[data-placeholder] {
        color: ${colors.text.placeholder};
    }

    &[data-state='open'] {
        ${focusStyles()}
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    &[data-state='open'] ${Arrow} {
        transform: rotate(-180deg);
    }
`

export const SelectContent = styled(Content)`
    width: var(--radix-select-trigger-width);
    max-height: min(250px, 50vh);
    border: 1px solid ${colors.border.secondary};
    background: ${colors.background.default};
    overflow: hidden;

    &[data-side='bottom'] {
        border-bottom-right-radius: ${radii.arc8};
        border-bottom-left-radius: ${radii.arc8};
    }

    &[data-side='top'] {
        border-top-left-radius: ${radii.arc8};
        border-top-right-radius: ${radii.arc8};
    }

    &[data-state='open'] {
        ${focusStyles()}
    }
`

export const SelectOption = styled(Item)`
    ${text({variant: 'paragraph-base', color: 'muted'})}
    padding: ${tokens.spacing12} ${tokens.spacing20};

    &[data-highlighted],
    &[data-state='checked'] {
        color: ${colors.text.default};
        background: ${colors.background.muted};
    }
`

export default Select
